import moment from "moment";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useTenant } from "../context/TenantContext";
import { useLabels } from "../utils/labels";
import {
  MdDelete,
  MdEdit,
  MdVisibility,
  MdMoreVert,
  MdPhone,
  MdEmail,
  MdFilterList,
  MdSearch,
  MdDownload,
  MdViewColumn,
  MdClear,
} from "react-icons/md";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Avatar,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  Select,
  FormControl,
  InputLabel,
  Paper,
  Stack,
} from "@mui/material";
import { getData } from "../utils/serverHelper";
import { PATIENT_SCHEMA } from "../utils/urls";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Whatsapp from "../images/whatsapp.png";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

const PatientTable = ({
  setEditMode,
  setAddMode,
  onDeletePatient,
  onWhatsapp,
  onViewPatient,
}) => {
  const selector = useSelector((state) => state.business);
  const { tenant } = useTenant();
  const labels = useLabels();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [viewFilter, setViewFilter] = useState("all");
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Load from localStorage or use default
    const saved = localStorage.getItem("patientTableVisibleColumns");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return null;
      }
    }
    return null;
  });

  const handleMenuOpen = (event, rowData) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(rowData);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    if (selectedRow) {
      setAddMode(selectedRow);
    }
    handleMenuClose();
  };

  const handleView = () => {
    if (selectedRow && onViewPatient) {
      onViewPatient(selectedRow);
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedRow) {
      onDeletePatient(selectedRow.patient_id);
    }
    handleMenuClose();
  };

  const handleWhatsappClick = (name, contact) => {
    const number = "+91 " + contact;
    const message = "Hi " + name;
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${number}&text=${encodeURIComponent(
      message
    )}`;
    window.open(whatsappUrl, "_blank");
  };

  const getStatusChip = useCallback((isActive) => {
    return (
      <Chip
        label={isActive ? "Active" : "Inactive"}
        color={isActive ? "success" : "default"}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  }, []);

  const getGenderIcon = useCallback((gender) => {
    switch (gender?.toLowerCase()) {
      case "male":
        return "♂";
      case "female":
        return "♀";
      default:
        return "⚧";
    }
  }, []);

  // Fetch schema from backend
  useEffect(() => {
    const fetchSchema = async () => {
      try {
        const response = await getData(PATIENT_SCHEMA);
        if (response?.data) {
          // Flatten schema into array of columns
          const columns = [];
          Object.keys(response.data).forEach((category) => {
            Object.keys(response.data[category]).forEach((fieldKey) => {
              const field = response.data[category][fieldKey];
              columns.push({
                field: field.fieldName,
                displayName: field.displayName,
                category: category,
                type: field.type,
                ...field,
              });
            });
          });
          setAvailableColumns(columns);

          // Initialize visible columns if not set
          if (!visibleColumns) {
            const defaultVisible = [
              "full_name",
              "contact",
              "email",
              "age",
              "gender",
              "blood_group",
              "joined_date",
              "is_active",
              "actions",
            ];
            setVisibleColumns(defaultVisible);
            localStorage.setItem(
              "patientTableVisibleColumns",
              JSON.stringify(defaultVisible)
            );
          }
        }
      } catch (error) {
        console.error("Error fetching schema:", error);
        // Use default columns if schema fetch fails
        const defaultColumns = [
          { field: "full_name", displayName: `${labels.customer || "Customer"} Name` },
          { field: "contact", displayName: "Contact" },
          { field: "email", displayName: "Email" },
          { field: "age", displayName: "Age" },
          { field: "gender", displayName: "Gender" },
          { field: "blood_group", displayName: "Blood Group" },
          { field: "joined_date", displayName: "Joined Date" },
          { field: "is_active", displayName: "Status" },
        ];
        setAvailableColumns(defaultColumns);
        if (!visibleColumns) {
          const defaultVisible = [
            "full_name",
            "contact",
            "email",
            "age",
            "gender",
            "blood_group",
            "joined_date",
            "is_active",
            "actions",
          ];
          setVisibleColumns(defaultVisible);
        }
      }
    };
    fetchSchema();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build column definition based on field type
  const buildColumnDef = useCallback(
    (field) => {
      const baseDef = {
        field: field.field,
        headerName:
          field.displayName ||
          field.field
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase()),
        sortable: true,
        filter: true,
      };

      // Custom renderers for specific fields
      switch (field.field) {
        case "full_name":
          return {
            ...baseDef,
            headerName: "Full Name",
            flex: 1,
            minWidth: 240,
            cellRenderer: (params) => (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  py: 0.5,
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: "#667eea",
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  {params.data?.full_name?.charAt(0)?.toUpperCase() || "P"}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "#1a1a1a",
                      lineHeight: 1.4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {params.value || "N/A"}
                  </Typography>
                  {params.data?.patient_id_number && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#6c757d",
                        fontSize: "0.75rem",
                        lineHeight: 1.2,
                        display: "block",
                        mt: 0.25,
                      }}
                    >
                      ID: {params.data.patient_id_number}
                    </Typography>
                  )}
                </Box>
              </Box>
            ),
          };
        case "contact":
          return {
            ...baseDef,
            headerName: "Primary Contact",
      flex: 1,
      minWidth: 150,
            cellRenderer: (params) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MdPhone size={16} color="#667eea" />
                <Typography variant="body2">{params.value || "N/A"}</Typography>
              </Box>
            ),
          };
        case "email":
          return {
            ...baseDef,
            headerName: "Email Address",
            flex: 1,
            minWidth: 200,
            cellRenderer: (params) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <MdEmail size={16} color="#667eea" />
                <Typography
                  variant="body2"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "200px",
                  }}
                >
                  {params.value || "N/A"}
                </Typography>
              </Box>
            ),
          };
        case "age":
          return {
            ...baseDef,
            width: 100,
            cellRenderer: (params) => {
              const age = params.value;
              const dob = params.data?.date_of_birth;
              if (!age && dob) {
                const calculatedAge = moment().diff(moment(dob), "years");
                return calculatedAge > 0 ? `${calculatedAge} yrs` : "N/A";
              }
              return age ? `${age} yrs` : "N/A";
            },
          };
        case "gender":
          return {
            ...baseDef,
            width: 120,
            cellRenderer: (params) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontSize: "1.2rem" }}>
                  {getGenderIcon(params.value)}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ textTransform: "capitalize" }}
                >
                  {params.value || "N/A"}
                </Typography>
              </Box>
            ),
          };
        case "blood_group":
          return {
            ...baseDef,
            width: 130,
            cellRenderer: (params) =>
              params.value ? (
                <Chip
                  label={params.value}
                  size="small"
                  sx={{
                    bgcolor: "#ffebee",
                    color: "#c62828",
                    fontWeight: 600,
                    border: "1px solid #c62828",
                  }}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  N/A
                </Typography>
              ),
          };
        case "joined_date":
        case "registration_date":
        case "date_of_birth":
          return {
            ...baseDef,
            width: 150,
            valueFormatter: (params) =>
              params.value ? moment(params.value).format("DD MMM YYYY") : "N/A",
          };
        case "is_active":
          return {
            ...baseDef,
            width: 120,
            headerName: "Status",
            cellRenderer: (params) =>
              getStatusChip(params.value !== undefined ? params.value : 1),
          };
        default:
          // Default renderer for other fields
          if (field.type === "DATE") {
            return {
              ...baseDef,
              width: 150,
              valueFormatter: (params) =>
                params.value
                  ? moment(params.value).format("DD MMM YYYY")
                  : "N/A",
            };
          }
          return {
            ...baseDef,
      flex: 1,
      minWidth: 150,
            cellRenderer: (params) => (
              <Typography variant="body2">{params.value || "N/A"}</Typography>
            ),
          };
      }
    },
    [getGenderIcon, getStatusChip, handleMenuOpen]
  );

  const filteredPatients = useMemo(() => {
    // Ensure patients is always an array
    let patients = selector?.patients;
    if (!Array.isArray(patients)) {
      patients = [];
    }

    // Apply view filter
    if (viewFilter === "active") {
      patients = patients.filter((p) => p.is_active !== 0);
    } else if (viewFilter === "inactive") {
      patients = patients.filter((p) => p.is_active === 0);
    } else if (viewFilter === "new") {
      patients = patients.filter((p) => {
        if (!p.joined_date) return false;
        return moment(p.joined_date).isSame(moment(), "month");
      });
    }

    // Apply search filter
    if (searchText) {
      const search = searchText.toLowerCase();
      patients = patients.filter(
        (patient) =>
          patient.full_name?.toLowerCase().includes(search) ||
          patient.contact?.includes(search) ||
          patient.email?.toLowerCase().includes(search) ||
          patient.patient_id_number?.toLowerCase().includes(search)
      );
    }

    return patients;
  }, [selector?.patients, searchText, viewFilter]);

  // Build columnDefs dynamically based on visible columns
  const columnDefs = useMemo(() => {
    const baseColumns = [];

    // Add checkbox column (no field needed for checkbox selection)
    baseColumns.push({
      headerName: "",
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: "left",
      sortable: false,
      filter: false,
      suppressMenu: true,
      lockPosition: true,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    });

    // Always ensure we have at least default columns
    if (
      !visibleColumns ||
      !Array.isArray(visibleColumns) ||
      visibleColumns.length === 0 ||
      availableColumns.length === 0
    ) {
      // Return default columns if schema not loaded yet
      baseColumns.push({
        field: "full_name",
        headerName: "Full Name",
      flex: 1,
        minWidth: 240,
        cellRenderer: (params) => (
          <Box
            sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}
          >
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#667eea",
                fontSize: "0.875rem",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              {params.data?.full_name?.charAt(0)?.toUpperCase() || "P"}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: "#1a1a1a",
                  lineHeight: 1.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {params.value || "N/A"}
              </Typography>
              {params.data?.patient_id_number && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "#6c757d",
                    fontSize: "0.75rem",
                    lineHeight: 1.2,
                    display: "block",
                    mt: 0.25,
                  }}
                >
                  ID: {params.data.patient_id_number}
                </Typography>
              )}
            </Box>
          </Box>
        ),
        sortable: true,
        filter: true,
      });

      // Add actions column
      baseColumns.push({
        field: "actions",
        headerName: "",
        width: 50,
        pinned: "right",
        cellRenderer: (params) => (
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, params.data)}
            sx={{ color: "#666" }}
          >
            <MdMoreVert size={18} />
          </IconButton>
        ),
        sortable: false,
        filter: false,
        suppressMenu: true,
        lockPosition: true,
      });

      return baseColumns;
    }

    // Add visible columns from schema
    visibleColumns.forEach((fieldName) => {
      if (fieldName !== "actions") {
        const field = availableColumns.find((col) => col.field === fieldName);
        if (field) {
          baseColumns.push(buildColumnDef(field));
        }
      }
    });

    // Always add actions column at the end
    if (!baseColumns.some((col) => col.field === "actions")) {
      baseColumns.push({
        field: "actions",
        headerName: "",
        width: 50,
        pinned: "right",
        cellRenderer: (params) => (
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, params.data)}
            sx={{ color: "#666" }}
          >
            <MdMoreVert size={18} />
          </IconButton>
        ),
        sortable: false,
        filter: false,
        suppressMenu: true,
        lockPosition: true,
      });
    }

    // Ensure we always have at least checkbox and actions columns
    if (baseColumns.length < 2) {
      console.error("ColumnDefs is too short:", baseColumns);
      // Fallback to minimal columns
      return [
        {
          headerName: "",
          width: 50,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          pinned: "left",
          sortable: false,
          filter: false,
        },
        {
          field: "full_name",
          headerName: `${labels.customer || "Customer"} Name`,
          flex: 1,
          minWidth: 200,
        },
        {
          field: "actions",
          headerName: "",
          width: 50,
          pinned: "right",
          cellRenderer: (params) => (
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, params.data)}
              sx={{ color: "#666" }}
            >
              <MdMoreVert size={18} />
            </IconButton>
          ),
      sortable: false,
          filter: false,
        },
      ];
    }
    console.log(
      "ColumnDefs generated:",
      baseColumns.length,
      baseColumns.map((c) => c.field || c.headerName)
    );
    return baseColumns;
  }, [visibleColumns, availableColumns, buildColumnDef, handleMenuOpen]);

  const defaultColDef = useMemo(
    () => ({
    sortable: true,
    filter: true,
      resizable: true,
      floatingFilter: false,
    }),
    []
  );

  const stats = useMemo(() => {
    // Ensure patients is always an array
    let patients = selector?.patients;
    if (!Array.isArray(patients)) {
      patients = [];
    }
    return {
      total: patients.length,
      active: patients.filter((p) => p.is_active !== 0).length,
      inactive: patients.filter((p) => p.is_active === 0).length,
      newThisMonth: patients.filter((p) => {
        if (!p.joined_date) return false;
        return moment(p.joined_date).isSame(moment(), "month");
      }).length,
    };
  }, [selector?.patients]);

  const handleActionsMenuOpen = (event) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  return (
    <>
    <Paper 
      elevation={0} 
      sx={{ 
        border: 'none',
        borderRadius: 2,
        overflow: 'hidden',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {/* Search and Filter Bar */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
        <Stack 
          direction={{ xs: 'column', md: 'row' }} 
          spacing={2} 
          alignItems={{ xs: 'stretch', md: 'center' }}
        >
          {/* Search */}
          <TextField
            fullWidth
            size="small"
            placeholder={`Search ${labels.customers?.toLowerCase() || "customers"} by name, email, phone, or ID...`}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch style={{ color: '#6b7280' }} />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ p: 0.5 }}
                  >
                    <MdClear size={18} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              flex: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#ffffff',
                borderRadius: 1,
                '&:hover': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#d1d5db',
                  },
                },
                '&.Mui-focused': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#667eea',
                  },
                },
              },
            }}
          />

          {/* View Filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Filter</InputLabel>
            <Select
              value={viewFilter}
              label="Filter"
              onChange={(e) => setViewFilter(e.target.value)}
              sx={{
                borderRadius: 1,
                backgroundColor: '#ffffff',
              }}
            >
              <MenuItem value="all">All {labels.customers || "Customers"}</MenuItem>
              <MenuItem value="active">Active {labels.customers || "Customers"}</MenuItem>
              <MenuItem value="inactive">Inactive {labels.customers || "Customers"}</MenuItem>
              <MenuItem value="new">New This Month</MenuItem>
            </Select>
          </FormControl>

          {/* Edit Columns Button */}
          <Button
            variant="outlined"
            size="small"
            startIcon={<MdViewColumn />}
            onClick={() => setColumnDialogOpen(true)}
            sx={{
              textTransform: 'none',
              borderColor: '#d1d5db',
              color: '#6b7280',
              whiteSpace: 'nowrap',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6',
              },
            }}
          >
            Columns
          </Button>
        </Stack>

        {/* Results Count */}
        <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredPatients.length} {labels.customer?.toLowerCase() || "customer"}{filteredPatients.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          minHeight: 500,
          p: 2,
        }}
      >
            <div
              className="ag-theme-alpine"
              style={{
                width: "100%",
                height: "calc(100vh - 450px)",
                minHeight: 500,
                background: "transparent",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {columnDefs && columnDefs.length > 0 ? (
      <AgGridReact
                  rowData={filteredPatients || []}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        pagination={true}
                  paginationPageSize={20}
                  paginationPageSizeSelector={[10, 20, 50, 100]}
                  paginationAutoPageSize={false}
        animateRows={true}
        theme={"legacy"}
                  rowHeight={52}
                  headerHeight={44}
                  suppressRowClickSelection={true}
                  enableCellTextSelection={true}
                  ensureDomOrder={true}
                  rowSelection="multiple"
                  suppressCellFocus={true}
                  domLayout="normal"
                  suppressMenuHide={true}
                  enableRangeSelection={false}
                />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    p: 3,
                  }}
                >
                  <Typography>Loading columns...</Typography>
                </Box>
              )}
            </div>
      </Box>
    </Paper>

    {/* Actions Menu */}
      <Menu
        anchorEl={actionsMenuAnchor}
        open={Boolean(actionsMenuAnchor)}
        onClose={handleActionsMenuClose}
        PaperProps={{
          sx: {
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
            borderRadius: "12px",
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={handleActionsMenuClose}>
          <MdDownload style={{ marginRight: 8 }} />
          Export Selected
        </MenuItem>
        <MenuItem onClick={handleActionsMenuClose}>
          <MdDelete style={{ marginRight: 8 }} />
          Delete Selected
        </MenuItem>
      </Menu>

      {/* Action Menu with Glass UI */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{
          sx: {
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
            borderRadius: "12px",
            mt: 1,
          },
        }}
      >
        <MenuItem onClick={handleView}>
          <MdVisibility style={{ marginRight: 8 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEdit}>
          <MdEdit style={{ marginRight: 8 }} />
          Edit {labels.customer || "Customer"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedRow) {
              handleWhatsappClick(selectedRow.full_name, selectedRow.contact);
            }
            handleMenuClose();
          }}
        >
          <img
            src={Whatsapp}
            alt="WhatsApp"
            style={{ width: 20, height: 20, marginRight: 8 }}
          />
          Send WhatsApp
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: "error.main" }}>
          <MdDelete style={{ marginRight: 8 }} />
          Delete {labels.customer || "Customer"}
        </MenuItem>
      </Menu>

      {/* Column Visibility Dialog with Glass UI */}
      <Dialog
        open={columnDialogOpen}
        onClose={() => setColumnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.15)",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Columns
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select which columns to display in the table
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {/* Select All / Deselect All */}
            <Box sx={{ mb: 2, pb: 2, borderBottom: 1, borderColor: "divider" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={
                      visibleColumns &&
                      availableColumns.length > 0 &&
                      visibleColumns.filter((col) => col !== "actions")
                        .length === availableColumns.length
                    }
                    indeterminate={
                      visibleColumns &&
                      availableColumns.length > 0 &&
                      visibleColumns.filter((col) => col !== "actions").length >
                        0 &&
                      visibleColumns.filter((col) => col !== "actions").length <
                        availableColumns.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allColumns = [
                          ...availableColumns.map((col) => col.field),
                          "actions",
                        ];
                        setVisibleColumns(allColumns);
                      } else {
                        setVisibleColumns(["actions"]); // Keep actions column
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Select All
                  </Typography>
                }
              />
            </Box>

            {/* Column List by Category */}
            <List sx={{ maxHeight: 400, overflow: "auto" }}>
              {availableColumns.length > 0 ? (
                availableColumns.map((column) => (
                  <ListItem key={column.field} sx={{ px: 0, py: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={
                            visibleColumns?.includes(column.field) || false
                          }
                          onChange={(e) => {
                            if (e.target.checked) {
                              setVisibleColumns((prev) => [
                                ...(prev || []),
                                column.field,
                              ]);
                            } else {
                              setVisibleColumns(
                                (prev) =>
                                  prev?.filter((col) => col !== column.field) ||
                                  []
                              );
                            }
                          }}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {column.displayName ||
                              column.field
                                .replace(/_/g, " ")
                                .replace(/\b\w/g, (char) => char.toUpperCase())}
                          </Typography>
                          {column.category && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {column.category
                                .replace(/([A-Z])/g, " $1")
                                .replace(/^./, (str) => str.toUpperCase())}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={{ width: "100%" }}
                    />
                  </ListItem>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ p: 2, textAlign: "center" }}
                >
                  Loading columns...
                </Typography>
              )}

              {/* Actions column (always visible but show in list) */}
              <ListItem sx={{ px: 0, py: 0.5 }}>
                <FormControlLabel
                  control={<Checkbox checked={true} disabled />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        Actions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Always visible
                      </Typography>
                    </Box>
                  }
                  sx={{ width: "100%", opacity: 0.6 }}
                />
              </ListItem>
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button
            onClick={() => {
              // Reset to default
              const defaultVisible = [
                "full_name",
                "contact",
                "email",
                "age",
                "gender",
                "blood_group",
                "joined_date",
                "is_active",
                "actions",
              ];
              setVisibleColumns(defaultVisible);
            }}
            sx={{ borderRadius: "8px" }}
          >
            Reset to Default
          </Button>
          <Button
            onClick={() => {
              localStorage.setItem(
                "patientTableVisibleColumns",
                JSON.stringify(visibleColumns)
              );
              setColumnDialogOpen(false);
            }}
            variant="contained"
            sx={{
              borderRadius: "8px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)",
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export const PatientTableMobile = ({
  setEditMode,
  setAddMode,
  onDeletePatient,
  onViewPatient,
}) => {
  const selector = useSelector((state) => state.business);
  const { tenant } = useTenant();
  const labels = useLabels();
  const [searchText, setSearchText] = useState("");

  const filteredPatients = useMemo(() => {
    // Ensure patients is always an array
    let patients = selector?.patients;
    if (!Array.isArray(patients)) {
      patients = [];
    }
    if (!searchText) return patients;
    const search = searchText.toLowerCase();
    return patients.filter(
      (patient) =>
        patient.full_name?.toLowerCase().includes(search) ||
        patient.contact?.includes(search) ||
        patient.email?.toLowerCase().includes(search)
    );
  }, [selector?.patients, searchText]);

  return (
    <Box sx={{ width: "100%", display: { xs: "block", lg: "none" } }}>
      <TextField
        placeholder={`Search ${labels.customers?.toLowerCase() || "customers"}...`}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MdSearch />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {filteredPatients.map((patient) => (
          <Box
            key={patient.patient_id}
            sx={{
              p: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              mb: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "start",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar sx={{ bgcolor: "#667eea", width: 40, height: 40 }}>
                  {patient.full_name?.charAt(0)?.toUpperCase() || "P"}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {patient.full_name || "N/A"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {patient.contact || "No contact"}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <IconButton
                  size="small"
                  onClick={() => onViewPatient && onViewPatient(patient)}
                >
                  <MdVisibility size={18} />
                </IconButton>
                <IconButton size="small" onClick={() => setAddMode(patient)}>
                  <MdEdit size={18} />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDeletePatient(patient.patient_id)}
                >
                  <MdDelete size={18} color="error" />
                </IconButton>
              </Box>
            </Box>
            {patient.email && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                {patient.email}
              </Typography>
            )}
            <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
              {patient.blood_group && (
                <Chip label={patient.blood_group} size="small" />
              )}
              {patient.gender && (
                <Chip label={patient.gender} size="small" variant="outlined" />
              )}
              <Chip
                label={patient.is_active !== 0 ? "Active" : "Inactive"}
                size="small"
                color={patient.is_active !== 0 ? "success" : "default"}
              />
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PatientTable;
